/**
 * Users list — admin gerencia + impersona.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Ban, Eye, Loader2, ShieldOff, UserCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { PageHeader } from "@/components/page-header";
import { RolePill } from "@/components/status-pill";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { toAuthUser, usersApi, type UserListItem } from "@/lib/api-users";
import { formatRelative } from "@/lib/format";
import { useAuth, type UserRole } from "@/stores/auth";

export function UsersPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user: currentUser, startImpersonation } = useAuth();

  const list = useQuery({
    queryKey: ["users-all"],
    queryFn: () => usersApi.list({ size: 200 }),
  });

  const impersonate = useMutation({
    mutationFn: (id: string) => usersApi.impersonate(id),
    onSuccess: (data) => {
      startImpersonation(toAuthUser(data.target), data.tokens);
      toast.success(`Navegando como ${data.target.email}`);
      qc.invalidateQueries();
      navigate("/", { replace: true });
    },
    onError: (err) => {
      toast.error("Falha ao impersonar", {
        description: err instanceof Error ? err.message : "Erro",
      });
    },
  });

  const block = useMutation({
    mutationFn: ({ id, blocked }: { id: string; blocked: boolean }) =>
      usersApi.updateBlock(id, blocked),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users-all"] });
      toast.success("Atualizado");
    },
  });

  const columns: ColumnDef<UserListItem>[] = [
    {
      id: "user",
      accessorFn: (u) => u.name ?? u.email,
      header: "Usuário",
      cell: ({ row }) => (
        <div className="flex items-center gap-3 min-w-0">
          <Avatar size="md">
            {row.original.picture_url && (
              <AvatarImage src={row.original.picture_url} referrerPolicy="no-referrer" />
            )}
            <AvatarFallback name={row.original.name ?? row.original.email} />
          </Avatar>
          <div className="min-w-0">
            <p className="font-medium truncate">{row.original.name ?? "Sem nome"}</p>
            <p className="text-xs text-muted-foreground truncate">{row.original.email}</p>
          </div>
        </div>
      ),
    },
    {
      id: "role",
      accessorKey: "role",
      header: "Papel",
      cell: ({ row }) => <RolePill role={row.original.role as UserRole} />,
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) => {
        if (row.original.is_blocked) {
          return (
            <Badge variant="danger" dot>
              <ShieldOff className="h-3 w-3" /> Bloqueado
            </Badge>
          );
        }
        if (row.original.onboarding_completed) {
          return (
            <Badge variant="success" dot>
              <UserCheck className="h-3 w-3" /> Ativo
            </Badge>
          );
        }
        return <Badge variant="warning" dot>Setup pendente</Badge>;
      },
    },
    {
      id: "last_login_at",
      accessorKey: "last_login_at",
      header: "Último login",
      cell: ({ row }) => (
        <span className="text-xs text-muted-foreground">{formatRelative(row.original.last_login_at)}</span>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const isSelf = row.original.id === currentUser?.id;
        const isLoadingThis =
          impersonate.isPending && impersonate.variables === row.original.id;
        return (
          <div className="flex items-center gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="sm"
              disabled={isSelf || row.original.is_blocked || isLoadingThis}
              onClick={() => impersonate.mutate(row.original.id)}
              title={isSelf ? "Não pode impersonar a si mesmo" : "Impersonar"}
            >
              {isLoadingThis ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Eye className="h-3.5 w-3.5" />
              )}
              Impersonar
            </Button>
            {!isSelf && (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() =>
                  block.mutate({ id: row.original.id, blocked: !row.original.is_blocked })
                }
                title={row.original.is_blocked ? "Desbloquear" : "Bloquear"}
                className="text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10"
              >
                <Ban className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader
        title="Usuários"
        description="Gerenciar contas, papéis e impersonação."
      />
      <DataTable
        columns={columns}
        data={list.data?.items ?? []}
        loading={list.isLoading}
        searchPlaceholder="Buscar por email, nome..."
        emptyTitle="Nenhum usuário"
        columnLabels={{
          user: "Usuário",
          role: "Papel",
          status: "Status",
          last_login_at: "Último login",
          actions: "Ações",
        }}
      />
    </>
  );
}
